import { Box, Button, Modal, Text, Tooltip } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { useNotifications } from '@fullstory/react-hooks';
import React, { PropsWithChildren } from 'react';

export interface AuditLogToolTipProps {
  changeType: string;
  changeValue: string;
  className?: string;
  placement?: React.ComponentProps<typeof Tooltip>['placement'];
}

export const AuditLogToolTip: React.FC<PropsWithChildren<AuditLogToolTipProps>> = ({
  changeValue,
  changeType,
  children,
  className,
  placement,
}) => (
  <Tooltip
    data-component="AuditLogToolTip"
    testId="audit-log-tooltip"
    placement={placement || 'top-start'}
    className={className}
    showDelay={300}
    persistDelay={100}
    maxWidth={500}
    tooltipContent={<AuditLogTooltipContainer changeType={changeType} changeValue={changeValue} />}
    variant="light"
  >
    {children}
  </Tooltip>
);

export const AuditLogTooltipContainer: React.FC<AuditLogToolTipProps> = ({
  changeType,
  changeValue,
}) => {
  const notifications = useNotifications();

  return (
    <Box
      display="flex"
      data-component="AuditLogTooltipContainer"
      flexDirection="column"
      // Adding minimum values for consistency across tooltips with smaller/larger change values
      minWidth={300}
      minHeight={100}
    >
      <Modal.Header data-component="AuditLogTooltipContainer">
        <Text appearance="bodyMedium">{changeType}</Text>
      </Modal.Header>
      <Modal.Body>
        <Box
          display="flex"
          flexDirection="column"
          overflowY="auto"
          maxWidth={350}
          maxHeight={350}
          padding={4}
        >
          <Text appearance="bodySmall">{changeValue}</Text>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Box display="flex" justifyContent="flex-end">
          <Button
            title={intl.formatMessage({ defaultMessage: 'Copy value', id: 'yDg/H+' })}
            iconLeft="copy"
            variant="minimal"
            onClick={() => {
              navigator.clipboard.writeText(changeValue).then(
                () => {
                  notifications.success(
                    intl.formatMessage({ defaultMessage: 'Copied value.', id: '/5/ot+' }),
                  );
                },
                () => {
                  notifications.error(
                    intl.formatMessage({ defaultMessage: 'Unable to copy value.', id: 'taSNLA' }),
                  );
                },
              );
            }}
            size="small"
          >
            <Text appearance="bodySmall">
              {intl.formatMessage({ defaultMessage: 'Copy value', id: 'yDg/H+' })}{' '}
            </Text>
          </Button>
        </Box>
      </Modal.Footer>
    </Box>
  );
};
